import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import DoneIcon from "@mui/icons-material/Done";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useWebServer } from "../../../../providers";
import usePolling from "../../../../lib/usePollingHook";
import {
  fetchActivityAlerts,
  setActivityAlertType,
  markActivityAlertsRead,
  fetchActivityAlertSettings,
  updateActivityAlertSettings,
} from "../../../../actions";
import { environmentMapper } from "../../../../utils/environment";
import {
  ACTIVITY_REFRESH_INTERVAL_MS,
  ACTIVITY_MAX_REFRESH_ROWS,
  ACTIVITY_ALERT_MIN_INTERVAL_MINUTES,
  ACTIVITY_ALERT_MAX_INTERVAL_MINUTES,
} from "./constants";
import { formatLastCapture, formatTimestamp } from "./format";

const isValidIntervalValue = (value) =>
  value === "" ||
  (/^\d+$/.test(value) &&
    Number(value) >= ACTIVITY_ALERT_MIN_INTERVAL_MINUTES &&
    Number(value) <= ACTIVITY_ALERT_MAX_INTERVAL_MINUTES);

const fontSx = {
  fontSize: ["0.75rem", "0.75rem", "0.75rem", "0.75rem", "1rem", "1rem"],
};

// Amber means one thing across the Activity tab: a lifecycle condition that is true
// right now, from the row's healthWarnings. This list is alert history, read or not,
// so it keeps red for maintenance and stays neutral for lifecycle rather than reuse
// amber for "an alert of that type was once raised".
const alertBorderColor = (alert) =>
  alert?.alertType === "maintenance" ? "status.danger" : "grey.400";

const AlertMarker = ({ alert }) =>
  alert?.alertType === "maintenance" ? (
    <PriorityHighIcon
      fontSize="small"
      sx={{ color: "status.danger", verticalAlign: "middle", mr: "0.25rem" }}
    />
  ) : null;

// the same healthWarnings that light the fleet row's triangle, so opening the device
// always shows why the row is amber - whichever alert tab is selected, and whether or
// not the alerts behind it have been read
const HealthWarnings = ({ warnings }) =>
  warnings?.length ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "0.5rem",
        mb: "1rem",
        p: "0.75rem",
        border: "1px solid",
        borderColor: "status.warning",
        borderRadius: "0.5rem",
      }}
    >
      <WarningAmberIcon sx={{ color: "status.warning" }} />
      <Box>
        {warnings.map((warning) => (
          <Typography key={warning.code} sx={fontSx}>
            {warning.message}
          </Typography>
        ))}
      </Box>
    </Box>
  ) : null;

export const deriveAlertSummary = (alert) => {
  if (alert?.message) {
    return alert.message;
  }
  if (alert?.kind === "shadow") {
    const keys = Object.keys(alert?.raw || {});
    return keys.length > 0 ? `shadow: ${keys.join(", ")}` : "shadow";
  }
  return alert?.kind || "-";
};

const SpecCell = ({ label, value }) => (
  <Grid size={4}>
    <Typography sx={fontSx} className={"text-gray-600"}>
      {label}
    </Typography>
    <Typography sx={fontSx}>{value ?? "-"}</Typography>
  </Grid>
);

const Index = ({
  device,
  onClose,
  alertType,
  alerts = [],
  alertsCursor,
  alertsLoading,
  alertsError,
  alertSettings,
  alertSettingsLoading,
  alertSettingsError,
  alertSettingsUpdating,
  alertSettingsUpdateError,
  fetchActivityAlerts,
  setActivityAlertType,
  markActivityAlertsRead,
  fetchActivityAlertSettings,
  updateActivityAlertSettings,
}) => {
  const { sendOrganizationRequest } = useWebServer();

  const refreshingRef = useRef(false);
  const loadingMoreRef = useRef(false);

  const deviceId = device?.deviceId;

  useEffect(() => {
    if (deviceId) {
      fetchActivityAlerts(sendOrganizationRequest, deviceId, {
        alertType,
      });
    }
  }, [deviceId, alertType, sendOrganizationRequest, fetchActivityAlerts]);

  useEffect(() => {
    if (deviceId) {
      fetchActivityAlertSettings(sendOrganizationRequest, deviceId);
    }
  }, [deviceId, sendOrganizationRequest, fetchActivityAlertSettings]);

  // null means untouched, so the saved value shows
  const [intervalDraft, setIntervalDraft] = useState(null);
  useEffect(() => {
    setIntervalDraft(null);
  }, [deviceId]);

  // so a save that lands after the user switched devices never clears the new device's draft
  const deviceIdRef = useRef(deviceId);
  useEffect(() => {
    deviceIdRef.current = deviceId;
  });

  const refreshRef = useRef({});
  useEffect(() => {
    refreshRef.current = {
      sendOrganizationRequest,
      alertType,
      loadedAlerts: alerts.length,
    };
  });

  usePolling(
    () => {
      const {
        sendOrganizationRequest: send,
        alertType: currentAlertType,
        loadedAlerts,
      } = refreshRef.current;
      // see ActivityMonitor: a refresh re-requests every page already loaded, so
      // paging no longer parks the poll for good
      if (
        refreshingRef.current ||
        loadingMoreRef.current ||
        loadedAlerts > ACTIVITY_MAX_REFRESH_ROWS
      ) {
        return;
      }
      refreshingRef.current = true;
      fetchActivityAlerts(send, deviceId, {
        alertType: currentAlertType,
        limit: loadedAlerts || undefined,
        background: true,
      }).finally(() => {
        refreshingRef.current = false;
      });
    },
    ACTIVITY_REFRESH_INTERVAL_MS,
    !!deviceId
  );

  if (!device) {
    return null;
  }

  // defence in depth: a stale alertSettings for a device the user has since
  // left never reaches render, even for a frame
  const settings =
    alertSettings?.deviceId === deviceId ? alertSettings : null;
  const savedIntervalValue =
    settings?.minIntervalMinutes == null
      ? ""
      : String(settings.minIntervalMinutes);
  const intervalValue = intervalDraft ?? savedIntervalValue;
  const muted = settings?.muted === true;
  const canSaveInterval =
    !!settings &&
    !alertSettingsUpdating &&
    !muted &&
    intervalDraft !== null &&
    isValidIntervalValue(intervalDraft) &&
    intervalDraft !== savedIntervalValue;

  const handleSaveInterval = () => {
    const savedFor = deviceId;
    updateActivityAlertSettings(sendOrganizationRequest, deviceId, {
      minIntervalMinutes: intervalDraft === "" ? null : Number(intervalDraft),
    }).then((ok) => {
      if (ok && deviceIdRef.current === savedFor) {
        setIntervalDraft(null);
      }
    });
  };

  const desired = device.desired || {};
  const reported = device.reported || {};

  const handleLoadMore = () => {
    if (loadingMoreRef.current || alertsLoading || !alertsCursor) {
      return;
    }
    loadingMoreRef.current = true;
    fetchActivityAlerts(sendOrganizationRequest, device.deviceId, {
      alertType,
      cursor: alertsCursor,
    }).finally(() => {
      loadingMoreRef.current = false;
    });
  };

  const handleMarkRead = (alert) => {
    markActivityAlertsRead(
      sendOrganizationRequest,
      device.deviceId,
      { ids: [alert._id] },
      { organizationId: device.organizationId, alertType: alert.alertType }
    );
  };

  // "Mark all read" is a server-side operation over the whole device, so gating it
  // on the alerts currently paged in would disable it whenever the unread ones sit
  // on a later page. The fleet row carries an authoritative maintenance count;
  // lifecycle has no equivalent, so that tab falls back to what is loaded.
  const hasUnread =
    alertType === "maintenance"
      ? device.unreadMaintenanceCount > 0
      : alerts.some((alert) => !alert.read);

  const handleMarkAllRead = () => {
    markActivityAlertsRead(
      sendOrganizationRequest,
      device.deviceId,
      { all: true, alertType },
      { organizationId: device.organizationId, alertType }
    );
  };

  return (
    <Dialog open={!!device} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.75rem" }}>
          <Typography component="span" sx={{ fontSize: "1.25rem" }}>
            {device.deviceId}
          </Typography>
          <Typography component="span" sx={fontSx} className={"text-gray-600"}>
            {device.displayName}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <HealthWarnings warnings={device.healthWarnings} />
        <Grid container spacing={2}>
          <SpecCell
            label="State machine"
            value={desired.state_machine ?? reported.state_machine}
          />
          <SpecCell
            label="Last capture"
            value={formatLastCapture(device.lastCaptureAt)}
          />
          <SpecCell
            label="Last alert"
            value={formatTimestamp(device.last_alert_timestamp)}
          />
          <SpecCell
            label="Firmware"
            value={desired.firmware_version ?? reported.firmware_version}
          />
          <SpecCell
            label="Algorithm / Region"
            value={
              desired.alg_version ??
              reported.alg_version ??
              desired.vector_type ??
              reported.vector_type
            }
          />
          <SpecCell
            label="Environment"
            value={environmentMapper(desired.env ?? reported.env) || null}
          />
          <SpecCell label="Hardware ID" value={device.hardwareId} />
          <SpecCell label="Display name" value={device.displayName} />
          <SpecCell
            label="Organization"
            value={
              device.organizationId === "__unassigned__"
                ? "No organization"
                : device.organizationName
            }
          />
        </Grid>

        <Box sx={{ mt: "1.5rem" }}>
          <Typography sx={fontSx}>Chat and email alerts</Typography>
          <Typography sx={fontSx} className={"text-gray-600"}>
            New alerts for this device are sent to Google Chat and email.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={
                  settings
                    ? settings.muted === true
                    : device.alertsMuted === true
                }
                disabled={!settings || alertSettingsUpdating}
                onChange={(e) =>
                  updateActivityAlertSettings(sendOrganizationRequest, deviceId, {
                    muted: e.target.checked,
                  })
                }
              />
            }
            label="Mute alerts"
          />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: "0.75rem",
            }}
          >
            <TextField
              size="small"
              type="number"
              label="Minimum time between messages"
              placeholder="Default"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">min</InputAdornment>
                ),
              }}
              inputProps={{
                min: ACTIVITY_ALERT_MIN_INTERVAL_MINUTES,
                max: ACTIVITY_ALERT_MAX_INTERVAL_MINUTES,
                step: 1,
              }}
              sx={{ width: "15rem" }}
              value={intervalValue}
              onChange={(e) => setIntervalDraft(e.target.value)}
              disabled={!settings || alertSettingsUpdating || muted}
              error={!isValidIntervalValue(intervalValue)}
            />
            {/* a small outlined input's height, so it lines up with the field */}
            <Button
              size="small"
              sx={{ height: "2.5rem" }}
              disabled={!canSaveInterval}
              onClick={handleSaveInterval}
            >
              Save
            </Button>
          </Box>
          {alertSettingsLoading && !settings ? (
            <CircularProgress size="1rem" sx={{ my: "0.5rem" }} />
          ) : null}
          {alertSettingsError ? (
            <Typography sx={fontSx} className={"text-gray-600"}>
              Could not load alert settings.
            </Typography>
          ) : null}
          {alertSettingsUpdateError ? (
            <Typography sx={fontSx} className={"text-gray-600"}>
              Could not save alert settings.
            </Typography>
          ) : null}
        </Box>

        <Box sx={{ mt: "1.5rem" }}>
          <ToggleButtonGroup
            value={alertType}
            exclusive
            size="small"
            onChange={(e, value) => {
              if (value !== null) {
                setActivityAlertType(value);
              }
            }}
          >
            <ToggleButton value="maintenance">Maintenance</ToggleButton>
            <ToggleButton value="lifecycle">Lifecycle</ToggleButton>
          </ToggleButtonGroup>

          <List>
            {alerts.length === 0 ? (
              alertsLoading ? (
                <CircularProgress size="1.5rem" sx={{ my: "1rem" }} />
              ) : (
                <Typography sx={fontSx} className={"text-gray-600"}>
                  {alertsError
                    ? "Could not load alerts. Retry in a moment."
                    : "No alerts"}
                </Typography>
              )
            ) : (
              alerts.map((alert) => (
                <ListItem
                  key={alert._id}
                  sx={{
                    borderLeft: alert.read ? "none" : "4px solid",
                    borderColor: alertBorderColor(alert),
                    fontWeight: alert.read ? "normal" : "bold",
                    color: alert.read ? "text.secondary" : "text.primary",
                  }}
                  secondaryAction={
                    !alert.read ? (
                      <Tooltip title="Mark read">
                        <IconButton onClick={() => handleMarkRead(alert)}>
                          <DoneIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                >
                  <Box>
                    <Typography sx={fontSx} component="span">
                      <AlertMarker alert={alert} />
                      {formatTimestamp(alert.timestamp)} · {alert.alertType}
                      {"  "}
                      <Chip
                        size="small"
                        label={alert.read ? "Read" : "Unread"}
                        color={alert.read ? "default" : "error"}
                      />
                    </Typography>
                    <Typography
                      sx={{
                        ...fontSx,
                        fontWeight: alert.read ? "normal" : "bold",
                      }}
                    >
                      {deriveAlertSummary(alert)}
                    </Typography>
                  </Box>
                </ListItem>
              ))
            )}
          </List>

          {alertsCursor ? (
            <Button onClick={handleLoadMore} disabled={alertsLoading}>
              Load more
            </Button>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleMarkAllRead} disabled={!hasUnread}>
          Mark all read
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  const {
    alertType,
    alerts,
    alertsCursor,
    alertsLoading,
    alertsError,
    alertSettings,
    alertSettingsLoading,
    alertSettingsError,
    alertSettingsUpdating,
    alertSettingsUpdateError,
  } = state?.activityMonitor || {};
  return {
    alertType,
    alerts,
    alertsCursor,
    alertsLoading,
    alertsError,
    alertSettings,
    alertSettingsLoading,
    alertSettingsError,
    alertSettingsUpdating,
    alertSettingsUpdateError,
  };
};

export default connect(mapStateToProps, {
  fetchActivityAlerts,
  setActivityAlertType,
  markActivityAlertsRead,
  fetchActivityAlertSettings,
  updateActivityAlertSettings,
})(Index);
